import { useCallback, useEffect, useMemo } from 'react';
import { FCX, Tooltip } from '@datassential/platform-ui-lib';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import cn from 'classnames';
import HomeSectionHeader from '@pages/Home/components/HomeSectionHeader';
import HomeSectionTitle from '@pages/Home/components/HomeSectionTitle';
import HomeSectionSubTitle from '@pages/Home/components/HomeSectionSubTitle';
import { useHomeWorkspace } from '@pages/Home/context/HomeWorkspaceContext';
import { useChainSocialData } from '@apiHooks/useChainSocialData';
import { useHomeConsumerRatings } from '@apiHooks/useHomeConsumerRatings';
import ConsumerRatings from './ConsumerRatings/ConsumerRatings';
import BrandPerceptionsSocialPanel from './BrandPerceptionsSocialPanel';
import './HomeBrandPerceptions.scss';
import useLocalStorageState from "@hooks/useLocalStorageState";

const SOCIAL_TOOLTIP_ID = 'brand-perceptions-social-info';
export const HOME_WORKSPACE_PRESET_KEY = 'home--workspace-preset-id';

const SECTION_ID = 'brand-perceptions';

const HomeBrandPerceptions: FCX = () => {
    const { chainId, chainName, registerSection, unregisterSection } = useHomeWorkspace();

    const idValueConverter = useCallback(
        (value: string | null): number | null => {
            if (value === null || value === 'null') return null;
            const num = Number(value);
            if (isNaN(num)) return null;
            return num;
        },
        [],
    );

    const [selectedPresetId, setSelectedPresetId] = useLocalStorageState<number | null>(
        HOME_WORKSPACE_PRESET_KEY,
        null,
        idValueConverter
    );

    const { data: chainData, isLoading: isChainDataLoading } = useChainSocialData(chainId);
    const { data: consumerRatingsData, isLoading: isConsumerRatingsLoading } = useHomeConsumerRatings(chainId);

    const hasSocialData = useMemo(
        () => !isChainDataLoading && chainData.hasData,
        [isChainDataLoading, chainData.hasData],
    );
    const hasConsumerRatings = useMemo(
        () => !isConsumerRatingsLoading && (consumerRatingsData.presets?.length ?? 0) > 0,
        [isConsumerRatingsLoading, consumerRatingsData.presets],
    );
    const isAnyLoading = isChainDataLoading || isConsumerRatingsLoading;
    const hasAnyData = hasSocialData || hasConsumerRatings;

    useEffect(() => {
        if (isAnyLoading) return;
        if (hasAnyData) {
            registerSection(SECTION_ID);
        } else {
            unregisterSection(SECTION_ID);
        }
        return () => unregisterSection(SECTION_ID);
    }, [isAnyLoading, hasAnyData, registerSection, unregisterSection]);

    if (!hasAnyData) return null;

    return (
        <div className="Home__wrapper">
            <HomeSectionHeader>
                <HomeSectionTitle testId="textBrandPerceptionsTitle">
                    {chainName} Brand Perceptions
                </HomeSectionTitle>
            </HomeSectionHeader>
            <div className="Home__sub-wrapper pad-bottom">
                {hasSocialData && (
                    <>
                        <div className="HomeBrandPerceptions__social-header">
                            <HomeSectionSubTitle testId="textBrandPerceptionsSocialTitle">
                                Social: {chainName}
                            </HomeSectionSubTitle>
                            <AiOutlineInfoCircle
                                className="HomeBrandPerceptions__info-icon"
                                data-tooltip-id={SOCIAL_TOOLTIP_ID}
                            />
                            <Tooltip id={SOCIAL_TOOLTIP_ID} place="right" style={{ zIndex: 1 }}>
                                <div className="HomeBrandPerceptions__tooltip">
                                    <div style={{ marginBottom: 5 }}><b>Data Details</b></div>
                                    <ul>
                                        <li>updated weekly</li>
                                        <li>cleaned and vetted by Datassential trend experts</li>
                                        <li>sources include: TikTok, Instagram, Pinterest, YouTube, X, Reddit</li>
                                        <li>reflects US & Canadian social activity</li>
                                    </ul>
                                </div>
                            </Tooltip>
                        </div>
                        <BrandPerceptionsSocialPanel
                            chainId={chainId}
                            chainName={chainName}
                            chainData={chainData}
                            isLoading={isChainDataLoading}
                        />
                    </>
                )}
                <ConsumerRatings
                    data={consumerRatingsData}
                    selectedPresetId={selectedPresetId}
                    setSelectedPresetId={setSelectedPresetId}
                    isLoading={isConsumerRatingsLoading}
                    className={cn(!hasSocialData && 'no-margin')}
                />
            </div>
        </div>
    );
};

export default HomeBrandPerceptions;
